import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, X, Plus, Check } from 'lucide-react';
import { addImagesToSet, getImageSets, listSets, removeImageFromSet } from '../../api/sets';
import { imageUrl } from '../../api/client';
import { timeAgo } from '../../utils/timeAgo';
import type { SetSummary } from '../../api/setsTypes';
import { CreateSetModal } from './CreateSetModal';

interface AddToSetPopoverProps {
  workspaceId: string;
  // Bild-IDn att lägga till. Kan vara 1 (från hover-pill) eller många (selection mode).
  imageIds: string[];
  // Stäng popover utan att göra något.
  onClose: () => void;
  // Anropas efter lyckad bulk-add med antal sets bilderna lades till i.
  onAdded?: (setCount: number) => void;
  // Positionering: 'bottom' (default) = popover öppnas uppåt från en action bar,
  // 'right' = öppnas till höger om en hover-pill (för enskild bild).
  anchor?: 'bottom' | 'right';
  // Optional anchor element för smart positionering — om saknas centreras popover.
  anchorRect?: DOMRect | null;
}

/**
 * AddToSetPopover — checkbox-list för att lägga bilder i ett eller flera sets.
 *
 * Sortering: Recent (top 3 senaste) sedan "All sets". Search filtrerar live.
 * "Create new…" längst ner öppnar CreateSetModal med pre-fylld search-text.
 *
 * Vi bulk-stagear "checked"-state lokalt och commitar ALLA val på Done — så
 * man kan toggle:a fram och tillbaka utan att skicka API-anrop per klick.
 */
export function AddToSetPopover({
  workspaceId,
  imageIds,
  onClose,
  onAdded,
  anchor = 'bottom',
  anchorRect,
}: AddToSetPopoverProps) {
  const queryClient = useQueryClient();
  const popoverRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [checked, setChecked] = useState<Set<string>>(new Set());
  // Initial membership (set-IDn bilden redan är med i). Bara meningsfullt
  // när en enda bild är vald — för multi-image vore det tvetydigt "är någon
  // av dessa bilder redan i set X?". Vi laddar bara för single-image-fallet.
  const [initialMembership, setInitialMembership] = useState<Set<string> | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const isSingleImage = imageIds.length === 1;
  const singleImageId = isSingleImage ? imageIds[0] : null;

  // Hämta sets för workspace
  const setsQuery = useQuery({
    queryKey: ['sets', workspaceId],
    queryFn: () => listSets(workspaceId, { sort: 'activity' }),
    enabled: !!workspaceId,
  });

  // Hämta nuvarande membership för enskild bild så att vi kan visa redan
  // tillagda sets som checked + tillåta uncheck = remove.
  const membershipQuery = useQuery({
    queryKey: ['image', singleImageId, 'sets'],
    queryFn: () => getImageSets(singleImageId!),
    enabled: !!singleImageId,
  });

  // När membership-data kommer in, pre-fill `checked` med dem. Vi kör detta
  // endast en gång (när data first loadar) så att lokala toggles inte
  // skrivs över av re-render.
  useEffect(() => {
    if (initialMembership !== null) return; // already initialized
    if (!isSingleImage) {
      // Multi-image: ingen membership att pre-fylla.
      setInitialMembership(new Set());
      return;
    }
    if (membershipQuery.data) {
      const ids = new Set(membershipQuery.data.map((m) => m.id));
      setInitialMembership(ids);
      setChecked(new Set(ids));
    }
  }, [membershipQuery.data, isSingleImage, initialMembership]);

  // Autofocus search — men INTE på touch-enheter (iPad/iPhone). På mobil
  // skulle autofocus trigga virtuellt tangentbord som tar halv skärm; bättre
  // att låta användaren tappa på sökfältet om de vill söka.
  useEffect(() => {
    // Detect touch-primary device. matchMedia('(hover: none)') är true för
    // enheter där pekaren saknar hover (= touch som primary input).
    const isTouchPrimary =
      typeof window !== 'undefined' &&
      window.matchMedia('(hover: none) and (pointer: coarse)').matches;
    if (!isTouchPrimary) {
      searchRef.current?.focus();
    }
  }, []);

  // Esc → stäng. Klick utanför → stäng (men inte om createOpen är true).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        if (createOpen) return; // CreateSetModal hanterar sin egen Esc
        onClose();
      }
    }
    function onClick(e: MouseEvent) {
      if (createOpen) return;
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose();
      }
    }
    window.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onClick);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onClick);
    };
  }, [onClose, createOpen]);

  const allSets = setsQuery.data ?? [];

  // Filtrera + sortera
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allSets;
    return allSets.filter((s) => {
      if (s.name.toLowerCase().includes(q)) return true;
      if (s.description?.toLowerCase().includes(q)) return true;
      if (s.tags.some((t) => t.includes(q))) return true;
      return false;
    });
  }, [allSets, query]);

  // Dela upp i Recent (top 3) + Others när det inte finns någon sökning
  const { recent, others } = useMemo(() => {
    if (query.trim()) return { recent: [], others: filtered };
    return {
      recent: filtered.slice(0, 3),
      others: filtered.slice(3),
    };
  }, [filtered, query]);

  // Diff mellan initial membership och nuvarande check-state ger oss vad
  // som ska läggas till respektive tas bort. Multi-image: vi gör bara add
  // (initialMembership är tom Set i det fallet, så ingen remove triggas).
  const baseline = initialMembership ?? new Set<string>();
  const toAddIds = useMemo(
    () => Array.from(checked).filter((id) => !baseline.has(id)),
    [checked, baseline],
  );
  const toRemoveIds = useMemo(
    () => (isSingleImage ? Array.from(baseline).filter((id) => !checked.has(id)) : []),
    [baseline, checked, isSingleImage],
  );
  const hasChanges = toAddIds.length > 0 || toRemoveIds.length > 0;

  // Apply mutation — kör add + remove parallellt, idempotent på server.
  const applyMutation = useMutation({
    mutationFn: async () => {
      const adds = toAddIds.map((id) => addImagesToSet(id, imageIds));
      // remove: vi vet redan att det är single-image om toRemoveIds har innehåll
      const removes = toRemoveIds.map((setId) =>
        removeImageFromSet(setId, singleImageId!),
      );
      const results = await Promise.allSettled([...adds, ...removes]);
      const failed = results.filter((r) => r.status === 'rejected');
      if (failed.length === results.length && results.length > 0) {
        throw new Error('Failed to update sets');
      }
      return { added: toAddIds.length, removed: toRemoveIds.length };
    },
    onSuccess: ({ added, removed }) => {
      queryClient.invalidateQueries({ queryKey: ['sets'] });
      queryClient.invalidateQueries({ queryKey: ['hub'] });
      // Set-vyer som påverkas
      [...toAddIds, ...toRemoveIds].forEach((setId) => {
        queryClient.invalidateQueries({ queryKey: ['set', setId] });
        queryClient.invalidateQueries({ queryKey: ['set', setId, 'images'] });
      });
      // Image membership chip i Detail
      if (singleImageId) {
        queryClient.invalidateQueries({ queryKey: ['image', singleImageId, 'sets'] });
      }
      onAdded?.(added + removed);
      onClose();
    },
  });

  function toggleCheck(setId: string) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(setId)) next.delete(setId);
      else next.add(setId);
      return next;
    });
  }

  const totalImages = imageIds.length;
  const numChecked = checked.size;
  const isManaging = isSingleImage && baseline.size > 0;

  // Positionering — om anchorRect ges, försök rama in popover smart.
  const positionStyle: React.CSSProperties = useMemo(() => {
    if (!anchorRect) {
      // Default: centrera horisontellt, fixed på botten/center
      return {
        position: 'fixed',
        left: '50%',
        transform: 'translateX(-50%)',
        bottom: anchor === 'bottom' ? 88 : undefined,
        top: anchor === 'right' ? '50%' : undefined,
      };
    }
    const PAD = 8;
    const W = 320;
    const H = 480; // estimate
    if (anchor === 'right') {
      // Höger om anchor, vertikalt centrerad
      const top = Math.max(PAD, anchorRect.top - 40);
      let left = anchorRect.right + PAD;
      if (left + W > window.innerWidth - PAD) {
        left = anchorRect.left - W - PAD;
      }
      return { position: 'fixed', top, left };
    }
    // Bottom-anchor: ovanför anchor
    const bottom = window.innerHeight - anchorRect.top + PAD;
    let left = anchorRect.left + anchorRect.width / 2 - W / 2;
    if (left < PAD) left = PAD;
    if (left + W > window.innerWidth - PAD) left = window.innerWidth - W - PAD;
    return { position: 'fixed', bottom, left, maxHeight: H };
  }, [anchorRect, anchor]);

  return (
    <>
      <div
        ref={popoverRef}
        className="z-[180] w-[320px] max-w-[calc(100vw-32px)] bg-loupe-bg-2/98 backdrop-blur-3xl border border-loupe-border-2 rounded-2xl shadow-[0_24px_60px_-10px_rgba(0,0,0,0.75)] overflow-hidden animate-msgIn"
        style={positionStyle}
        role="dialog"
        aria-modal="true"
        aria-label="Add to set"
      >
        {/* Head */}
        <div className="px-4 pt-3.5 pb-2.5 border-b border-loupe-border flex items-center justify-between gap-2.5">
          <div className="flex flex-col gap-0.5 min-w-0">
            <div className="text-[13px] font-semibold text-loupe-text flex items-center gap-2">
              <span className="w-[18px] h-[18px] bg-henrik/15 border border-henrik/30 rounded-[5px] flex items-center justify-center text-henrik text-[11px]">
                ⊞
              </span>
              {isManaging ? 'Manage sets' : 'Add to set'}
            </div>
            <div className="text-[11px] text-loupe-text-3 tabular-nums">
              {isManaging ? (
                <>
                  In{' '}
                  <strong className="text-henrik font-semibold">
                    {baseline.size} set{baseline.size === 1 ? '' : 's'}
                  </strong>{' '}
                  · toggle to add or remove
                </>
              ) : (
                <>
                  <strong className="text-henrik font-semibold">
                    {totalImages} frame{totalImages === 1 ? '' : 's'}
                  </strong>{' '}
                  to add
                </>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-7 h-7 bg-transparent text-loupe-text-3 hover:text-loupe-text hover:bg-loupe-bg-3 rounded-full transition-colors flex items-center justify-center"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>

        {/* Search */}
        <div className="px-3 pt-2.5 pb-2 border-b border-loupe-border">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-loupe-text-3 pointer-events-none" />
            <input
              ref={searchRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search or create…"
              className="w-full pl-8 pr-3 py-2.5 bg-loupe-bg-3 border border-loupe-border rounded-lg text-loupe-text text-[13px] outline-none transition-colors min-h-[36px] focus:border-henrik placeholder:text-loupe-text-3"
            />
          </div>
        </div>

        {/* List */}
        <div className="max-h-[280px] overflow-y-auto p-1.5">
          {setsQuery.isLoading ? (
            <div className="py-8 text-center text-xs text-loupe-text-3">
              Loading sets…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-7 px-4 text-center text-xs text-loupe-text-3">
              {query.trim() ? (
                <>
                  No matches for "<strong className="text-loupe-text-2">{query}</strong>"
                  <br />
                  <span className="text-[11px]">Try a different search, or create a new set.</span>
                </>
              ) : (
                <>No sets yet. Create your first set below.</>
              )}
            </div>
          ) : (
            <>
              {recent.length > 0 && !query.trim() ? (
                <>
                  <div className="text-[10px] tracking-[0.15em] uppercase text-loupe-text-3 px-2.5 pt-2 pb-1">
                    Recent
                  </div>
                  {recent.map((s) => (
                    <SetRow
                      key={s.id}
                      set={s}
                      checked={checked.has(s.id)}
                      onToggle={() => toggleCheck(s.id)}
                      showActivity
                    />
                  ))}
                </>
              ) : null}

              {others.length > 0 ? (
                <>
                  {recent.length > 0 && !query.trim() ? (
                    <div className="text-[10px] tracking-[0.15em] uppercase text-loupe-text-3 px-2.5 pt-2 pb-1">
                      All sets
                    </div>
                  ) : null}
                  {others.map((s) => (
                    <SetRow
                      key={s.id}
                      set={s}
                      checked={checked.has(s.id)}
                      onToggle={() => toggleCheck(s.id)}
                    />
                  ))}
                </>
              ) : null}
            </>
          )}
        </div>

        {/* Create new */}
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          className="flex items-center gap-2.5 px-3 py-3 border-t border-loupe-border text-henrik text-[13px] font-medium w-full text-left bg-transparent hover:bg-henrik/[0.06] transition-colors"
        >
          <span className="w-6 h-6 rounded-md bg-henrik/15 border border-dashed border-henrik/40 flex items-center justify-center text-henrik text-sm font-semibold">
            <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
          </span>
          <span className="flex-1 flex flex-col gap-0.5">
            <span>Create new set…</span>
            <span className="text-[11px] text-loupe-text-3 font-normal">
              Use these {totalImages} frame{totalImages === 1 ? '' : 's'}
            </span>
          </span>
        </button>

        {/* Foot */}
        <div className="px-3 py-2.5 border-t border-loupe-border bg-black/25 flex items-center justify-between gap-2">
          <div className="text-[11px] text-loupe-text-2 tabular-nums flex-1 min-w-0">
            {!hasChanges ? (
              numChecked === 0 ? (
                'Select a set or create new'
              ) : (
                'No changes'
              )
            ) : (
              <span className="flex flex-wrap items-center gap-x-1.5">
                {toAddIds.length > 0 ? (
                  <span>
                    +<strong className="text-henrik font-semibold">{toAddIds.length}</strong>
                  </span>
                ) : null}
                {toRemoveIds.length > 0 ? (
                  <span>
                    −<strong className="text-red-300 font-semibold">{toRemoveIds.length}</strong>
                  </span>
                ) : null}
                <span className="text-loupe-text-3">
                  set{toAddIds.length + toRemoveIds.length === 1 ? '' : 's'}
                </span>
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={() => applyMutation.mutate()}
            disabled={!hasChanges || applyMutation.isPending}
            className={[
              'px-3.5 py-2 rounded-full text-xs font-semibold tracking-wider transition-all min-h-[36px]',
              !hasChanges || applyMutation.isPending
                ? 'bg-white/30 text-black/40 cursor-not-allowed'
                : 'bg-white text-black hover:scale-[1.03]',
            ].join(' ')}
          >
            {applyMutation.isPending ? 'Saving…' : 'Done'}
          </button>
        </div>

        {applyMutation.isError ? (
          <div className="px-3 pb-2.5 text-[11px] text-red-300">
            {applyMutation.error instanceof Error ? applyMutation.error.message : 'Could not update'}
          </div>
        ) : null}
      </div>

      {/* CreateSetModal nested */}
      {createOpen ? (
        <CreateSetModal
          workspaceId={workspaceId}
          initialName={query.trim()}
          initialImageIds={imageIds}
          onClose={() => setCreateOpen(false)}
          onCreated={() => {
            setCreateOpen(false);
            onAdded?.(1);
            onClose();
          }}
        />
      ) : null}
    </>
  );
}

// ─── Set-rad i listan ─────────────────────────────────────────────────

interface SetRowProps {
  set: SetSummary;
  checked: boolean;
  onToggle: () => void;
  showActivity?: boolean;
}

function SetRow({ set, checked, onToggle, showActivity = false }: SetRowProps) {
  const accent = set.color ?? '#5fa8d3';
  return (
    <button
      type="button"
      onClick={onToggle}
      className={[
        'flex items-center gap-2.5 px-2.5 py-2 rounded-lg cursor-pointer transition-colors select-none w-full text-left',
        'hover:bg-loupe-bg-3',
      ].join(' ')}
    >
      <span
        className={[
          'w-5 h-5 rounded-md border flex-shrink-0 flex items-center justify-center text-white text-xs font-bold transition-all',
          checked
            ? 'bg-henrik border-henrik'
            : 'bg-transparent border-loupe-border-2 text-transparent',
        ].join(' ')}
      >
        {checked ? <Check className="w-3 h-3" /> : null}
      </span>

      <span
        className="relative w-8 h-8 rounded-md overflow-hidden bg-loupe-bg-3 flex-shrink-0"
        style={{ ['--set-accent' as string]: accent }}
      >
        <span
          className="absolute left-0 top-0 bottom-0 w-[3px] z-10"
          style={{ background: accent }}
        />
        {set.coverImageId ? (
          <img
            src={imageUrl(set.coverImageId, 'thumb')}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover"
          />
        ) : (
          <span
            className="w-full h-full flex items-center justify-center text-white/30 font-display text-base"
            style={{ background: `linear-gradient(135deg, ${accent} 0%, transparent 70%), #1a1a1a` }}
          >
            ⊞
          </span>
        )}
      </span>

      <span className="flex-1 min-w-0 flex flex-col gap-0.5">
        <span className="text-[13px] font-medium text-loupe-text truncate">
          {set.name}
        </span>
        <span className="text-[11px] text-loupe-text-3 tabular-nums truncate">
          {showActivity ? `${timeAgo(set.lastActivityAt)} · ` : ''}
          {set.imageCount} frame{set.imageCount === 1 ? '' : 's'}
        </span>
      </span>
    </button>
  );
}
